import axios from 'axios';

/*
 * Sales trial API. Same conventions as metrics.api.js: base URL resolved the way
 * rtkData.api.js does it, `headers` (plural) so the token actually arrives — the
 * server resolves the caller from the verified token only — and the
 * { data, error } envelope unwrapped into a value or a thrown Error.
 */

const API_BASE = () => {
  let host = process.env.REACT_APP_BASE_URL_LOC;
  if (process.env.REACT_APP_MODE === 'docker') {
    host = process.env.REACT_APP_BASE_URL_DOC;
  }
  let base = `http://${host}`;
  if (process.env.REACT_APP_DEPLOY === 'nginx') {
    base += '/api';
  } else if (process.env.REACT_APP_DEPLOY === 'local') {
    base += `:${process.env.REACT_APP_SERVER_PORT}/api`;
  }
  return base;
};

const post = async (path, idToken, body = {}) => {
  try {
    return await axios.post(`${API_BASE()}${path}`, { user: {}, ...body }, {
      headers: { authtoken: idToken, 'Content-Type': 'application/json' },
      withCredentials: true,
    });
  } catch (err) {
    // 4xx carry the same envelope; surface its message rather than axios's.
    if (err.response && err.response.data) return err.response;
    throw err;
  }
};

const unwrap = (res) => {
  const payload = res && res.data;
  if (!payload) throw new Error('Empty response');
  if (payload.error && payload.error.isError === '1') {
    throw new Error(payload.error.message || 'Request failed');
  }
  return payload.data;
};

/**
 * { canManage, environment, limits, targets? } — canManage:false for almost
 * everyone. `withTargets` also checks every environment/region this site can
 * create on (it asks each server, so the landing page leaves it off).
 */
export const fetchTrialAccess = async (idToken, withTargets = false) =>
  unwrap(await post('/trial/access', idToken, withTargets ? { withTargets: true } : {}));

/** counts: { 'dev:US': 2, 'staging:EU': 1 } */
export const createTrialApi = async (idToken, { companyName, counts, expiresAt }) =>
  unwrap(await post('/trial/create', idToken, { companyName, counts, expiresAt }));

export const fetchTrials = async (idToken) => unwrap(await post('/trial/list', idToken));

export const extendTrialApi = async (idToken, trialId, expiresAt) =>
  unwrap(await post('/trial/extend', idToken, { trialId, expiresAt }));

export const endTrialApi = async (idToken, trialId) => unwrap(await post('/trial/end', idToken, { trialId }));

export const resetTrialPasswordApi = async (idToken, trialId) =>
  unwrap(await post('/trial/reset-password', idToken, { trialId }));

/**
 * Add `count` accounts to one box ("staging:US") of a trial. `password` is the
 * trial's current password to keep a single one; omit it to have new accounts
 * get a fresh password, returned once.
 */
export const addTrialAccountsApi = async (idToken, trialId, target, count, password) =>
  unwrap(await post('/trial/accounts/add', idToken, { trialId, target, count, ...(password ? { password } : {}) }));

/** Delete one trial account for good. */
export const removeTrialAccountApi = async (idToken, trialId, target, email) =>
  unwrap(await post('/trial/accounts/remove', idToken, { trialId, target, email }));

/** Send the trial email (as edited on the page) to `to`, copying the server's TRIAL_EMAIL_CC. */
export const sendTrialEmailApi = async (idToken, { to, subject, body }) =>
  unwrap(await post('/trial/email/send', idToken, { to, subject, body }));
